/* eslint-disable max-len */
import { Divider, Switch, IconButton, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useNavigate, createSearchParams, useSearchParams } from 'react-router-dom';
import { FieldControllerTypes } from '../../components/form_v2/types';
import TableGridWithFilters from '../../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import Theme from '../../Themes/main.theme';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import HttpService from '../../services/HttpService';
import { createSearchParamsFormat } from '../../lib/helperFuncs';
import DialogAction from '../../components/Dialog/DialogAction';
import { useSnackbar } from 'notistack';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import { validationSchema } from '../../lib/static/professionals/validationSchema';
import { initialValues1 } from '../../lib/static/professionals/initialValues';
import AsyncBackdropSubmit from '../../components/AsyncBackdropSubmit';
import UserService from '../../services/UserService';
import { Permissions, PermissionsManageLocProf } from '../../lib/permissions';

function ProfessionalsDashboard() {
  const [specialties, setSpecialties] = useState([{ key: '', value: '' }]);
  const [services, setServices] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [status, setStatus] = useState({ id: null, is_published: false });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [onChangeLoading, setOnChangeLoading] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const onChangeProfessional = async (id: number | null) => {
    setOnChangeLoading(true);
    try {
      const resp = await HttpService.getAxiosClient().patch(`${process.env.REACT_APP_BACKEND_URL}/professionals/${id}`, {
        is_published: status.is_published
      });
      if (resp.status === 200) {
        enqueueSnackbar('Επιτυχής αλλαγή κατάστασης του/της Επαγγελματία/Επιχείρησης', { variant: 'success' });
        setRefetch(!refetch);
      }
      setOnChangeLoading(false);
    } catch (error) {
      console.error(error);
      setOnChangeLoading(false);
      enqueueSnackbar('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.', { variant: 'error' });
    }
  };

  const filterFields = [
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      customLabel: true,
      name: 'free_text',
      label: t('Ονοματεπώνυμο/Επωνυμία'),
      filtersDesign: true,
    },
    {
      control: FieldControllerTypes.multiselecttree,
      type: 'autocomplete',
      label: t('Ειδικότητα'),
      name: 'level2',
      filtersDesign: true,
      groupByElement: 'parent',
      options: specialties
    },
    {
      control: FieldControllerTypes.autocomplete,
      type: 'autocomplete',
      label: t('Κατηγορία Υπηρεσιών'),
      name: 'level1',
      filtersDesign: true,
      options: services
    }]
  ];


  const isSwitchEnabled = (row: any) => {
    {
      if (UserService.hasRole([PermissionsManageLocProf.PermissionInternalManagePublishProfessionalCreateMyOrg])) {
        return true;
      }
      if (UserService.hasRole([PermissionsManageLocProf.PermissionInternalManagePublishProfessionalCreateAll])) {
        return row.is_applicant === true;
      }
      return false;
    }
  };


  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Ονοματεπώνυμο/Επωνυμία',
      minWidth: 350,
      renderCell: ({ row }) => row?.['professionals:businessName']?.[0]?.['@value']
    },
    {
      field: 'specialty',
      headerName: 'Ειδικότητα',
      minWidth: 270,
      sortable: false,
      renderCell: ({ row }) => row?.['professionals:professionalSpecialtyLvl2']?.[0]?.['display_title'] || '-'
    },
    {
      field: 'services',
      headerName: 'Κατηγορία Επαγγελματιών',
      minWidth: 290,
      sortable: true,
      renderCell: ({ row }) => row?.['professionals:servicesCategory']?.[0]?.['@value'] || '-'
    },
    {
      field: 'action',
      headerName: 'Ενέργειες',
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) => <>
        <Tooltip
          disableHoverListener={
            !UserService.hasRole([
              Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateMy_org,
              Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateAll
            ]) && !row?.is_applicant
          }
          PopperProps={{
            disablePortal: true
          }}
          title={(UserService.hasRole([
            Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateMy_org,
            Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateAll
          ]) || row?.is_applicant)
            ? 'Επεξεργασία Επαγγελματία'
            : 'Δεν έχετε το δικαίωμα επεξεργασίας του Επαγγελματία'
          }>
          <span>
            <IconButton sx={{ mr: 1 }}
              disabled={!UserService.hasRole([
                Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateMy_org,
                Permissions.PermissionInternalProfessionalsApplicationModificationRequestCreateAll
              ]) && !row?.is_applicant
              }
              color='primary'
              onClick={() => navigate(`/admin/professionals/edit/${row['o:id']}?omeka=true`)}>
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip PopperProps={{
          disablePortal: true
        }}
        title='Προβολή'>
          <IconButton sx={{ mr: 1 }}
            color='primary'
            onClick={() => navigate(`/admin/professionals/details/${row['o:id']}`)}>
            <LaunchIcon />
          </IconButton>
        </Tooltip>
      </>
    },
    {
      field: 'isPublished',
      headerName: 'Publish / Unpublish',
      sortable: false,
      minWidth: 140,
      flex: 1,
      renderCell: ({ row }) =>
        <Switch
          checked={row['o:is_public']}
          disabled={!isSwitchEnabled(row)}
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
            setStatus({
              id: row['o:id'],
              is_published: !row['o:is_public']
            });
          }} />
    }
  ];

  const getSpecialtiesLvL2 = async () => {
    const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/professionals/parametrics/specialties`);
    const options_level_2 = resp?.data?.level2?.data?.map((location: any) => {
      const x = location?.['dcterms:relation'];
      if (!x) return;
      return {
        key: location?.['o:title'],
        value: location?.['o:id'],
        parent: x[0].display_title
      };
    }).filter((value: any) => value);
    setSpecialties(options_level_2.sort((a: any, b: any) => a.parent.localeCompare(b.parent, 'el')));
  };

  const getServices = async () => {
    const resp = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/professionals/parametrics/services`);
    const options = resp?.data.map((location: any) => {
      return {
        key: location,
        value: location
      };
    });
    setServices(options.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
  };

  const onSubmit = (values: any) => {
    const x = createSearchParamsFormat(values);
    Object.keys(initialValues1).map((value) => {
      searchParams.delete(value);
      setSearchParams(searchParams);
    });
    setSearchParams(`${searchParams.toString()}&${createSearchParams(x).toString()}`);
  };

  useEffect(() => {
    Promise.all([
      getServices(),
      getSpecialtiesLvL2()
    ]);
  }, []);


  return (
    <>
      <AsyncBackdropSubmit isLoading={onChangeLoading}>
        Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...
      </AsyncBackdropSubmit>
      <Typography sx={{ my: 2, mt: 4 }} variant='h2'
        color={Theme?.labelColor?.main}>{t('Διαχείριση Επαγγελματιών')}</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TableGridWithFilters
        reFetch={refetch}
        pageSize={15}
        rowsPerPageOptions={[15, 30, 45]}
        sortingMode='server'
        sortModel={[{ field: 'title', sort: 'asc' }]}
        paginationMode='server'
        responseDataFieldName='records'
        responseTotalCountFieldName='totalResults'
        changeFilters={(values: any) => onSubmit(values)}
        initialValues={initialValues1}
        filterFields={filterFields}
        validationSchema={validationSchema}
        columns={columns}
        resetFilters={true}
        fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/professionals/my-org`} />
      <DialogAction
        open={open} title={'Αλλαγή Κατάστασης'}
        handleClose={() => {
          setOpen(false);
        }}
        content='Είστε σίγουρος/η ότι θέλετε να αλλάξετε την κατάσταση;'
        onAccept={() => {
          onChangeProfessional(status.id);
          setOpen(false);
        }} />
    </>
  );
}

export default ProfessionalsDashboard;